import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import {
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Dropdown,
  FieldSet,
  Label,
  LabeledControl,
  Legend,
} from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';

import { getRecordFromURLSearchParams } from '../../utils';

import type { ChangeEvent } from 'react';
import type { FeeCategoryDictionaryItemDto, ModelDictionaryItemDto } from '@zg-rentals/ts-rental-manager-api-client';
type Props = {
  nonSectionedData?: Record<string, Partial<FeeCategoryDictionaryItemDto>>;
  sectionedData?: Record<
    string,
    {
      isRendered: boolean;
      heading: string;
      data: Record<string, Partial<FeeCategoryDictionaryItemDto & ModelDictionaryItemDto>>;
    }
  >;
  nonSectionedDataEscapes?: Array<string>;
  keyName: string;
  ctaText: string;
};

const CostsAndFeesFilterDropdown = ({
  nonSectionedData,
  sectionedData,
  keyName,
  ctaText,
  nonSectionedDataEscapes,
}: Props) => {
  const router = useRouter();

  const urlSearchParams = useMemo(() => new URLSearchParams(router.asPath.split('?')[1] || ''), [router.asPath]);

  const [renderDropdown, setRenderDropwdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, boolean>>({});

  const ctaCounts = useMemo(() => Object.keys(selectedFilters).length, [selectedFilters]);

  const handleShallowHistoryPush = useCallback(
    (urlSearchParams: URLSearchParams) => {
      const pathname = router.asPath.split('?')[0];
      const url = `${pathname}?${urlSearchParams.toString()}`;
      // @ts-expect-error - Next.js router.replace expects specific route types but we're building dynamic URLs
      router.replace(url, undefined, { shallow: true });
    },
    [router],
  );

  useEffect(() => {
    if (!renderDropdown) {
      setSelectedFilters(getRecordFromURLSearchParams({ urlSearchParams, keyName }));
    }
  }, [renderDropdown, urlSearchParams, keyName]);

  useEffect(() => {
    setSelectedFilters(getRecordFromURLSearchParams({ urlSearchParams, keyName }));
  }, [urlSearchParams, keyName]);

  if (!nonSectionedData && !sectionedData) {
    return null;
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const temp = { ...selectedFilters };

    if (temp[e.target.value]) {
      delete temp[e.target.value];
    } else {
      temp[e.target.value] = true;
    }

    setSelectedFilters(temp);
  };

  return (
    <Dropdown.Root onOpenChange={(open) => setRenderDropwdown(open)} open={renderDropdown}>
      <Dropdown.Trigger>
        <Button tone="neutral" icon={renderDropdown ? <IconChevronUpFilled /> : <IconChevronDownFilled />}>
          {ctaText}
          {ctaCounts > 0 ? ` (${ctaCounts})` : undefined}
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          data-testid={`CostsAndFeesFilterDropdown-${keyName}`}
          css={{
            maxWidth: 320,
            width: 320,
          }}
        >
          <FieldSet
            legend={<Legend css={{ srOnly: true }}>{ctaText}</Legend>}
            css={{
              maxHeight: 320,
              overflow: 'auto',
            }}
          >
            {nonSectionedData && (
              <Dropdown.Body>
                {Object.keys(nonSectionedData).map((feeKey) =>
                  !nonSectionedDataEscapes || !nonSectionedDataEscapes.includes(feeKey) ? (
                    <LabeledControl
                      key={`${keyName}-${nonSectionedData[feeKey]?.displayName}`}
                      label={<Label>{nonSectionedData[feeKey]?.displayName}</Label>}
                      control={<Checkbox value={feeKey} checked={selectedFilters[feeKey]} onChange={handleOnChange} />}
                    />
                  ) : null,
                )}
              </Dropdown.Body>
            )}

            {sectionedData &&
              Object.keys(sectionedData).map((feeKey) => {
                const { isRendered, heading, data } = sectionedData[feeKey];

                if (!isRendered) {
                  return null;
                }

                return (
                  <Fragment key={`sectionedData-${feeKey}`}>
                    <Dropdown.Heading level={2}>{heading}</Dropdown.Heading>

                    <Dropdown.Body>
                      {Object.keys(data).map((dataKey) => (
                        <LabeledControl
                          key={dataKey}
                          label={<Label>{data?.[dataKey]?.feedModelName || data?.[dataKey]?.displayName}</Label>}
                          control={
                            <Checkbox value={dataKey} checked={selectedFilters[dataKey]} onChange={handleOnChange} />
                          }
                        />
                      ))}
                    </Dropdown.Body>
                  </Fragment>
                );
              })}
          </FieldSet>
          <Divider />
          <Dropdown.Footer>
            <ButtonGroup
              data-testid={`${keyName}-dropdown`}
              aria-label={`${keyName} Dropdown`}
              fluid
              css={{ width: '100%' }}
            >
              <Dropdown.Close>
                <Button
                  onClick={() => {
                    urlSearchParams.delete(keyName);

                    handleShallowHistoryPush(urlSearchParams);
                  }}
                  tone="neutral"
                  emphasis="outlined"
                >
                  Clear
                </Button>
              </Dropdown.Close>

              <Dropdown.Close>
                <Button
                  onClick={() => {
                    urlSearchParams.delete(keyName);

                    const filterKeys = Object.keys(selectedFilters);
                    if (filterKeys.length > 0) {
                      urlSearchParams.set(keyName, filterKeys.toString());
                    }

                    handleShallowHistoryPush(urlSearchParams);
                  }}
                  tone="brand"
                  emphasis="filled"
                >
                  Apply
                </Button>
              </Dropdown.Close>
            </ButtonGroup>
          </Dropdown.Footer>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export default memo(CostsAndFeesFilterDropdown);
